import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, tap } from "rxjs";
import { BaseJobPollingService, PollingProcess } from "./base";
import { ProcessEvent } from "./polling";

type InputData = {
  file?: File;
  prompt?: string;
  templateType: "template_document" | "regular";
};

type RetryData = null;
type StartData = {
  templateId: string;
};

type CompleteData = null;

export type TemplateGenerationEvent = ProcessEvent<StartData, CompleteData>;

@Injectable({
  providedIn: "root",
})
export class TemplateGenerationService extends BaseJobPollingService<
  InputData,
  RetryData,
  StartData,
  CompleteData
> {
  private static POLLING_TIMEOUT = 600 * 1000; // 10 min
  private static POLLING_DELAY = 4 * 1000; // 4 sec
  private idToKey: Map<string, string>;

  constructor(protected http: HttpClient) {
    super(
      TemplateGenerationService.POLLING_TIMEOUT,
      TemplateGenerationService.POLLING_DELAY,
    );
    this.idToKey = new Map();
  }

  protected getKey(templateId: string): string {
    return this.idToKey.get(templateId) || "";
  }

  generate(
      templateType: "template_document" | "regular",
    file?: File,
    prompt?: string,
  ): Observable<ProcessEvent<StartData, CompleteData>> {
    const job = this.dispatch({ file, prompt, templateType });
    return job.process.pipe(
      tap((event) => {
        if (event.status === "start") {
          const templateId = event.data.data.templateId;
          this.idToKey.set(templateId, job.jobId);
        }
      }),
    );
  }

  regenerate(
    templateId: string,
  ): Observable<ProcessEvent<StartData, CompleteData>> {
    return this.retry(this.getKey(templateId), null).process;
  }

  currentProcess(
    templateId: string,
  ): Observable<ProcessEvent<StartData, null>> {
    return super.currentProcess(this.getKey(templateId));
  }

  protected _dispatchRequest(data: InputData): PollingProcess {
    const formData = new FormData();
    if (data.file) formData.append("file", data.file);
    if (data.prompt) formData.append("prompt", data.prompt);
    formData.append("template_type", data.templateType);
    return this.http.post<{
      requestId: string;
      templateId: string;
    }>("template-generation/generate", formData);
  }

  protected _retryRequest(prevTryData: {
    input: InputData;
    start?: StartData;
  }): PollingProcess {
    const file = prevTryData.input.file;
    const prompt = prevTryData.input.prompt;
    const templateType = prevTryData.input.templateType;
    const templateId = prevTryData.start?.templateId;
    const formData = new FormData();
    if (file) formData.append("file", file);
    if (prompt) formData.append("prompt", prompt);
    formData.append("template_type", templateType);
    formData.append("template_id", templateId);
    return this.http.post<{
      requestId: string;
      templateId: string;
    }>("template-generation/regenerate", formData);
  }

  protected _checkStatusRequest(
    requestId: string,
  ): Observable<{ status: string; data: null }> {
    return this.http
      .get<{ status: string }>(`template-generation/status/${requestId}`)
      .pipe(map((r) => ({ ...r, data: null })));
  }
}
